import React, { useCallback } from 'react';

import { cn } from '@lyearn/style';
import { Stack, StackProps } from '@lyearn/ui';

import { useMultiStepContext } from '../../context';

interface StepProps extends Omit<StackProps, 'children'> {
  id: string;
  activeClassName?: string;
  children: ((props: { isActive: boolean }) => React.ReactNode) | React.ReactNode;
  disabled?: boolean;
  error?: boolean;
  errorRenderer?: (id: string) => React.ReactNode;
}

const Step: React.FC<StepProps> = ({
  children,
  id,
  className,
  activeClassName = '',
  disabled,
  error,
  errorRenderer,
  ...stackProps
}) => {
  const { activeStepId, setStep } = useMultiStepContext();
  const isActive = activeStepId === id;
  const resolvedClassName = cn(
    className,
    { [activeClassName]: isActive && !disabled },
    {
      'cursor-pointer': !disabled,
      'cursor-not-allowed opacity-50': disabled,
    },
  );

  const onSetStep = useCallback(() => {
    if (disabled) return;

    setStep(id);
  }, [disabled, id, setStep]);

  if (typeof children === 'function') {
    return (
      <Stack {...stackProps} className={resolvedClassName} onClick={onSetStep}>
        {children({ isActive })}
        {error ? errorRenderer?.(id) : null}
      </Stack>
    );
  }

  return (
    <Stack {...stackProps} className={resolvedClassName} onClick={onSetStep}>
      {children}
      {error ? errorRenderer?.(id) : null}
    </Stack>
  );
};

export default Step;
